import React, { useState } from 'react';
import { FileText, Truck, CreditCard, Package } from 'lucide-react';
import type { PurchaseFlow, ActivityEntry } from '@/types/purchaseFlow';
import { useTranslation } from 'react-i18next';

interface ContextPanelProps {
  className?: string;
  flow: PurchaseFlow | null;
  activityEntries?: ActivityEntry[];
}

type Tab = 'draft' | 'activity';

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function fmt(n: number | undefined, currency: string): string {
  if (n == null) return '';
  return `${currency} ${n.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}

const ContextPanel: React.FC<ContextPanelProps> = ({ className, flow, activityEntries = [] }) => {
  const [tab, setTab] = useState<Tab>('draft');
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());
  const { t } = useTranslation('purchaseflow');

  if (!flow) {
    return (
      <div className={`flex-col h-full bg-gray-50 dark:bg-gray-800/50 ${className}`}>
        <div className="flex items-center justify-center h-full text-xs text-gray-400">
          {t('noFlowSelected')}
        </div>
      </div>
    );
  }

  const sd = flow.stage_data as Record<string, unknown>;
  console.log('[ContextPanel] render, items:', (sd?.items as Array<Record<string, unknown>>)?.map(i => ({ part_code: i.part_code, quantity: i.quantity, unit_price: i.unit_price })));
  const vendorName = (sd?.vendor_name as string) || (sd?.vendor as Record<string, string>)?.name || flow.vendor_code || '';
  const vendorCode = (sd?.vendor_code as string) || flow.vendor_code || '';
  const poNumber = sd?.po_number as string;
  const grandTotal = sd?.grand_total as number;
  const deliveryDate = sd?.expected_delivery as string;
  const deliveryAddress = sd?.delivery_address as string;
  const deliveryAddressLabel = sd?.delivery_address_label as string;
  const paymentTerms = (sd?.payment_terms as string) || '';
  const iqcType = sd?.iqc_type as string;
  const invoiceNumber = sd?.invoice_number as string;
  const paidDate = sd?.paid_date as string;
  const items = sd?.items as Array<{
    line_no?: number; product_name: string; specification?: string;
    quantity: number; unit_price: number; currency?: string;
  }> | undefined;
  const currency = 'TWD';

  const subtotal = items?.reduce((sum, it) => sum + (it.quantity * it.unit_price), 0) ?? 0;
  const taxAmount = subtotal * 0.05;
  const total = grandTotal ?? (subtotal > 0 ? subtotal + taxAmount : 0);

  const toggleExpand = (toolId: string) => {
    setExpandedTools(prev => {
      const next = new Set(prev);
      if (next.has(toolId)) next.delete(toolId);
      else next.add(toolId);
      return next;
    });
  };

  return (
    <div className={`flex-col h-full bg-gray-50 dark:bg-gray-800/50 ${className}`}>
      {/* Tab Bar */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
        {(['draft', 'activity'] as Tab[]).map(tabKey => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={`flex-1 px-3 py-2.5 text-xs font-medium transition-colors border-b-2 ${
              tab === tabKey
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
          >
            {tabKey === 'draft'
              ? (poNumber ? `📋 ${poNumber}` : `📋 ${t('documents.purchaseOrder')}`)
              : `${t('tabs.activity')}${activityEntries.length > 0 ? ` (${activityEntries.length})` : ''}`
            }
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">

        {/* ============ PO DRAFT TAB ============ */}
        {tab === 'draft' && (
          <div className="text-xs">
            {/* PO Header — blue gradient like PurchaseOrderDialog */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-white" />
                <div>
                  <div className="text-white font-semibold text-sm">
                    {poNumber || t('documents.purchaseOrder')}
                  </div>
                  <div className="text-blue-100 text-[10px]">
                    {flow.status === 'completed' ? t('completed') : vendorCode ? `${vendorName} (${vendorCode})` : t('documents.poNotCreated')}
                  </div>
                </div>
                <div className="ml-auto">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                    flow.status === 'active' ? 'bg-white/20 text-white' :
                    flow.status === 'paused' ? 'bg-amber-400/20 text-amber-100' :
                    flow.status === 'completed' ? 'bg-emerald-400/20 text-emerald-100' :
                    'bg-white/10 text-white/60'
                  }`}>
                    {flow.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Section: Vendor Info */}
            {vendorCode && (
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  廠商資料 Vendor
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <Field label="廠商代號" value={vendorCode} />
                  <Field label="廠商名稱" value={vendorName} />
                </div>
              </div>
            )}

            {/* Section: Order Info */}
            {(deliveryDate || paymentTerms || deliveryAddress) && (
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  <Truck className="w-3 h-3 inline mr-1" />訂單資料 Order
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  {deliveryDate && <Field label={t('summary.delivery')} value={deliveryDate} />}
                  {paymentTerms && <Field label="付款條件" value={paymentTerms} />}
                  {iqcType && <Field label={t('summary.iqc')} value={iqcType} capitalize />}
                  <Field label="幣別" value={currency} />
                </div>
                {deliveryAddress && (
                  <div className="mt-2 pt-2 border-t border-gray-50 dark:border-gray-700">
                    <Field label="收貨地址" value={`${deliveryAddressLabel ? deliveryAddressLabel + ' — ' : ''}${deliveryAddress}`} />
                  </div>
                )}
              </div>
            )}

            {/* Section: Line Items Table */}
            {items && items.length > 0 ? (
              <div className="border-b border-gray-100 dark:border-gray-700">
                <div className="px-4 pt-3 pb-1">
                  <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <Package className="w-3 h-3 inline mr-1" />採購項目 Items ({items.length})
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                        <th className="text-center px-2 py-1.5 font-medium w-6">#</th>
                        <th className="text-left px-2 py-1.5 font-medium">品名</th>
                        <th className="text-left px-2 py-1.5 font-medium">規格</th>
                        <th className="text-right px-2 py-1.5 font-medium w-14">數量</th>
                        <th className="text-right px-2 py-1.5 font-medium w-16">單價</th>
                        <th className="text-right px-2 py-1.5 font-medium w-20">金額</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => {
                        const amount = item.quantity * item.unit_price;
                        return (
                          <tr key={i} className="border-t border-gray-50 dark:border-gray-700 hover:bg-blue-50/50 dark:hover:bg-blue-900/10">
                            <td className="text-center px-2 py-1.5 text-gray-400">{item.line_no ?? i + 1}</td>
                            <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200 truncate max-w-[100px]" title={item.product_name}>
                              {item.product_name}
                            </td>
                            <td className="px-2 py-1.5 text-gray-500 dark:text-gray-400 truncate max-w-[100px]" title={item.specification || ''}>
                              {item.specification || '—'}
                            </td>
                            <td className="text-right px-2 py-1.5 font-mono text-gray-700 dark:text-gray-300">
                              {item.quantity.toLocaleString()}
                            </td>
                            <td className="text-right px-2 py-1.5 font-mono text-gray-700 dark:text-gray-300">
                              {item.unit_price.toFixed(2)}
                            </td>
                            <td className="text-right px-2 py-1.5 font-mono text-gray-700 dark:text-gray-300">
                              {amount.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-gray-400">
                <Package className="w-5 h-5 mx-auto mb-1 opacity-40" />
                尚未新增品項
              </div>
            )}

            {/* Section: Totals */}
            {subtotal > 0 && (
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <div className="flex flex-col items-end space-y-0.5">
                  <div className="flex justify-between w-36">
                    <span className="text-gray-500">小計</span>
                    <span className="font-mono text-gray-700 dark:text-gray-300">{fmt(subtotal, currency)}</span>
                  </div>
                  <div className="flex justify-between w-36">
                    <span className="text-gray-500">稅額 5%</span>
                    <span className="font-mono text-gray-700 dark:text-gray-300">{fmt(taxAmount, currency)}</span>
                  </div>
                  <div className="flex justify-between w-36 pt-1 border-t border-gray-200 dark:border-gray-600 font-semibold">
                    <span className="text-gray-700 dark:text-gray-300">{t('summary.total')}</span>
                    <span className="font-mono text-blue-600 dark:text-blue-400">{fmt(total, currency)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Section: Linked Documents */}
            {(flow.inbound_ids?.length || flow.invoice_id) && (
              <div className="px-4 py-3">
                <div className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                  <CreditCard className="w-3 h-3 inline mr-1" />相關文件 Documents
                </div>
                <div className="space-y-1.5">
                  {flow.inbound_ids?.map((id, i) => (
                    <div key={id} className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                      <span>{t('documents.inbound')} #{i + 1}</span>
                      <span className="font-mono text-[10px] text-gray-400 truncate">{id.slice(0, 12)}</span>
                    </div>
                  ))}
                  {flow.invoice_id && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      <span>{t('documents.invoiceDoc')}</span>
                      <span className="font-mono text-[10px]">{invoiceNumber || flow.invoice_id.slice(0, 12)}</span>
                      {paidDate ? (
                        <span className="ml-auto text-[10px] text-emerald-600">✓ {paidDate}</span>
                      ) : (
                        <span className="ml-auto text-[10px] text-amber-500">{t('summary.pending')}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============ ACTIVITY TAB ============ */}
        {tab === 'activity' && (
          <div className="p-3 space-y-2">
            {activityEntries.length === 0 ? (
              <div className="text-xs text-gray-400 text-center py-8">{t('activity.noToolCalls')}</div>
            ) : (
              activityEntries.map(entry => {
                const isExpanded = expandedTools.has(entry.toolId);
                return (
                  <div
                    key={entry.toolId}
                    className={`text-xs rounded-lg border px-3 py-2 transition-colors ${
                      entry.isCreate
                        ? 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/10'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <button onClick={() => toggleExpand(entry.toolId)} className="flex items-center gap-2 w-full text-left">
                      <span className="text-gray-400 font-mono w-10 flex-shrink-0">{formatTime(entry.timestamp)}</span>
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${entry.done ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
                      <span className="font-medium text-gray-700 dark:text-gray-300 truncate">
                        {t(`tools.${entry.toolName}`, { defaultValue: entry.toolName })}
                      </span>
                      <span className="ml-auto text-gray-400 flex-shrink-0">{isExpanded ? '▲' : '▼'}</span>
                    </button>
                    {isExpanded && entry.result && (
                      <div className="mt-2 ml-12 max-h-40 overflow-y-auto">
                        <ResultDisplay data={entry.result} />
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/** Render tool result as simple key-value UI instead of raw JSON */
const ResultDisplay: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
  // Skip internal/meta keys
  const skipKeys = new Set(['type', 'toolCallId']);

  const renderValue = (val: unknown): React.ReactNode => {
    if (val == null) return <span className="text-gray-300">—</span>;
    if (typeof val === 'boolean') return val ? <span className="text-emerald-600">✓</span> : <span className="text-gray-400">✗</span>;
    if (typeof val === 'number') return <span className="font-mono">{val.toLocaleString()}</span>;
    if (typeof val === 'string') return <span>{val}</span>;
    if (Array.isArray(val)) {
      if (val.length === 0) return <span className="text-gray-400 italic">empty</span>;
      // Array of objects → compact list
      if (typeof val[0] === 'object') {
        return (
          <div className="space-y-1 mt-0.5">
            {val.slice(0, 5).map((item, i) => {
              const obj = item as Record<string, unknown>;
              const label = (obj.product_name || obj.description || obj.name || obj.part_code || obj.inbound_number || obj.invoice_number || `#${i + 1}`) as string;
              const detail = (obj.quantity || obj.received_qty || obj.best_price || obj.total_amount || '') as string | number;
              return (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                  <span className="truncate">{String(label)}</span>
                  {detail && <span className="ml-auto font-mono text-gray-500 flex-shrink-0">{String(detail)}</span>}
                </div>
              );
            })}
            {val.length > 5 && <span className="text-gray-400 italic">+{val.length - 5} more</span>}
          </div>
        );
      }
      // Array of primitives
      return <span>{val.join(', ')}</span>;
    }
    if (typeof val === 'object') {
      // Nested object → recurse as indented block
      return <ResultDisplay data={val as Record<string, unknown>} />;
    }
    return <span>{String(val)}</span>;
  };

  const entries = Object.entries(data).filter(([k]) => !skipKeys.has(k));

  return (
    <div className="space-y-1 text-[10px]">
      {entries.map(([key, val]) => (
        <div key={key} className="flex gap-2">
          <span className="text-gray-400 flex-shrink-0 min-w-[60px]">{key.replace(/_/g, ' ')}</span>
          <div className="text-gray-600 dark:text-gray-400 min-w-0 flex-1">{renderValue(val)}</div>
        </div>
      ))}
    </div>
  );
};

/** Read-only field row */
const Field: React.FC<{ label: string; value: string; capitalize?: boolean }> = ({ label, value, capitalize }) => (
  <div className="flex flex-col">
    <span className="text-[9px] text-gray-400 dark:text-gray-500">{label}</span>
    <span className={`text-gray-700 dark:text-gray-300 ${capitalize ? 'capitalize' : ''}`}>{value || '—'}</span>
  </div>
);

export default ContextPanel;
